/**
 * Utility functions for HTML processing
 */

// Pre-compiled regex patterns for better performance
const HTML_TAG_REGEX = /<[^<>]*>/g
const LIST_REGEX = /<(ul|ol)(?:\s[^<>]*)?>[\s\S]*?<\/\1>/gi
const LIST_ITEM_REGEX = /<li(?:\s[^<>]*)?>[\s\S]*?<\/li>/gi
const BLOCK_ELEMENT_REGEX = /<\/(p|div|h[1-6])>/gi
const PUNCTUATION_REGEX = /[:.;!?]$/
const HTML_ENTITIES = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'"
}
const ENTITY_REGEX = /&(?:nbsp|amp|lt|gt|quot|#39);/g
const TRAILING_PUNCTUATION_REGEX = /[.;]\s*$/

/**
 * Strip HTML tags from a string and convert to plain text
 * Handles list items by converting them to semicolon-separated text
 * Cleans up HTML entities and normalizes whitespace
 *
 * @param {string} html - The HTML string to process
 * @returns {string} Clean plain text string
 */
export const stripHtmlTags = html => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  let result = html

  // Handle lists (both ul and ol) - convert to semicolon-separated text
  result = result.replace(LIST_REGEX, listMatch => {
    return listMatch.replace(LIST_ITEM_REGEX, liMatch => {
      // Extract content between <li> and </li> tags (liMatch is already the full match)
      const liContent = liMatch.replace(/<\/?li(?:\s[^<>]*)?>/gi, '')
      const cleanContent = liContent.replace(HTML_TAG_REGEX, '').trim()
      return cleanContent ? `${cleanContent}; ` : ''
    })
  })

  // Add periods after block elements if they don't end with punctuation
  result = result.replace(BLOCK_ELEMENT_REGEX, (match, tag, offset, string) => {
    const beforeTag = string.substring(0, offset).trim()
    return PUNCTUATION_REGEX.test(beforeTag) ? ' ' : '. '
  })

  // Remove all remaining HTML tags
  result = result.replace(HTML_TAG_REGEX, '')

  // Replace HTML entities using lookup table
  result = result.replace(ENTITY_REGEX, match => HTML_ENTITIES[match] || match)

  // Clean up whitespace and trailing punctuation
  return result
    .replace(TRAILING_PUNCTUATION_REGEX, '') // Remove trailing punctuation and spaces
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}
